import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk } from '../../store';
import * as AWS from 'aws-sdk/global';
import * as cognitoSDK from 'amazon-cognito-identity-js';
import { CognitoUserSession, CodeDeliveryDetails } from 'amazon-cognito-identity-js';
import { Dispatch } from 'react';

const userPoolId = process.env['REACT_APP_COGNITO_USER_POOL_ID'] ?? '';
const userPoolClientId = process.env['REACT_APP_COGNITO_CLIENT_ID'] ?? '';
const awsRegion = process.env['REACT_APP_REGION'] ?? '';

const cognitoUserPool = new cognitoSDK.CognitoUserPool({
  UserPoolId: userPoolId,
  ClientId: userPoolClientId,
});

let cognitoUser: cognitoSDK.CognitoUser;

interface IAccessToken {
  payload: {
    [key: string]: any;
  };
  attributes: any;
  jwtToken: string;
}

interface AuthState {
  accessToken: IAccessToken | null | undefined;
  mfaRequired: boolean;
  loading: boolean;
  loginError: string;
}

const initialState: AuthState = {
  accessToken: undefined,
  mfaRequired: false,
  loading: false,
  loginError: '',
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setState: (state, action: PayloadAction<IAccessToken | null>) => {
      state.accessToken = action.payload;
    },
    startLoading: state => {
      state.loginError = '';
      state.loading = true;
    },
    cancelLogin: state => {
      Object.assign(state, initialState);
      state.accessToken = null;
    },
    mfaRequired: (state, action: PayloadAction<CodeDeliveryDetails>) => {
      state.mfaRequired = true;
    },
    loginSuccess: (state, action: PayloadAction<IAccessToken>) => {
      state.mfaRequired = false;
      state.accessToken = action.payload;
      state.loading = false;
    },
    loginError: (state, action: PayloadAction<string>) => {
      state.mfaRequired = false;
      state.loginError = action.payload;
      state.loading = false;
    },
    logoutSuccess: state => {
      state.accessToken = null;
      state.loading = false;
    },
  },
});

export const {
  startLoading,
  setState,
  mfaRequired,
  cancelLogin,
  loginSuccess,
  loginError,
  logoutSuccess,
} = authSlice.actions;

async function onSuccess(dispatch: Dispatch<any>, session: CognitoUserSession) {
  AWS.config.region = awsRegion;
  const accessToken = session.getAccessToken() as any;
  const attributes = (await getAttributes(cognitoUser))?.map(attribute => ({ ...attribute }));
  dispatch(loginSuccess({ ...accessToken, attributes }));
}

export const login =
  (email: string, password: string): AppThunk =>
    dispatch => {
      dispatch(startLoading());
      email = (email || '').toLowerCase();
      const userData = {
        Username: email,
        Pool: cognitoUserPool,
      };
      cognitoUser = new cognitoSDK.CognitoUser(userData);
      const authenticationData = {
        Username: email,
        Password: password,
      };
      const authenticationDetails = new cognitoSDK.AuthenticationDetails(authenticationData);
      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (session: CognitoUserSession) => onSuccess(dispatch, session),
        onFailure: (error: Error) => dispatch(loginError(error.message)),
        mfaRequired: (codeDeliveryDetails: CodeDeliveryDetails) =>
          dispatch(mfaRequired(codeDeliveryDetails)),
      });
    };

export const sendMFACode =
  (mfaCode: string): AppThunk =>
    dispatch => {
      cognitoUser.sendMFACode(mfaCode, {
        onSuccess: (session: CognitoUserSession) => onSuccess(dispatch, session),
        onFailure: (error: Error) => dispatch(loginError(error.message)),
      });
    };

export const logout = (): AppThunk => dispatch => {
  dispatch(startLoading());
  const user = cognitoUserPool.getCurrentUser();
  if (user) {
    user.signOut();
  }
  dispatch(logoutSuccess());
};

export const loadUser = (): AppThunk => async dispatch => {
  try {
    const user = cognitoUserPool.getCurrentUser();
    if (user) {
      const session = await getSession(user);
      const attributes = (await getAttributes(user))?.map(attribute => ({ ...attribute }));
      const accessToken = session.getAccessToken() as any;
      dispatch(setState({ ...accessToken, attributes }));
    } else {
      dispatch(setState(null));
    }
  } catch (e) { }
};

export default authSlice.reducer;

export async function getAccessToken() {
  const user = cognitoUserPool.getCurrentUser();
  if (user) {
    const session = await getSession(user);
    const accessToken = session.getAccessToken();
    const expiration = accessToken.getExpiration();
    if (new Date(parseInt(expiration.toString() + '000')).getTime() < new Date().getTime()) {
      const refreshToken = session.getRefreshToken();
      await refreshSession(refreshToken);
    }
    return session.getAccessToken();
  }
  return null;
}

function getSession(user: cognitoSDK.CognitoUser): Promise<cognitoSDK.CognitoUserSession> {
  return new Promise((resolve, reject) => {
    user.getSession((error: Error | null, session: cognitoSDK.CognitoUserSession) =>
      error ? reject(error) : resolve(session)
    );
  });
}

function getAttributes(
  user: cognitoSDK.CognitoUser
): Promise<cognitoSDK.CognitoUserAttribute[] | undefined> {
  return new Promise((resolve, reject) => {
    user.getUserAttributes((error, attributes) => (error ? reject(error) : resolve(attributes)));
  });
}

function refreshSession(refreshToken: cognitoSDK.CognitoRefreshToken) {
  return new Promise((resolve, reject) => {
    cognitoUser.refreshSession(
      refreshToken,
      (error: Error | undefined, session: cognitoSDK.CognitoUserSession) =>
        error ? reject(error) : resolve(session)
    );
  });
}
